import React, { useState, useEffect } from 'react';
import { CreditCard, ArrowLeft, AlertCircle, ShieldAlert, CheckCircle, ExternalLink, Loader, Check, Sparkles, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isNative, openExternal } from '../../lib/native';
import { apiGet, apiPost } from '../../lib/api';
import './BillingPage.css';

function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingConfigured, setBillingConfigured] = useState(false);
  const [billingCards, setBillingCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardsError, setCardsError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState({}); // Per-card loading state
  const [processingPortal, setProcessingPortal] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    checkBillingConfig();
    checkEntitlement();
    fetchBillingCards();
  }, []);

  const checkBillingConfig = async () => {
    try {
      const response = await apiGet('/api/billing/config');
      if (response.ok) {
        const data = await response.json();
        setBillingConfigured(data.configured);
      }
    } catch (error) {
      console.error('Error checking billing config:', error);
    }
  };

  const checkEntitlement = async () => {
    try {
      const response = await apiGet('/api/user/entitlement');

      if (response.ok) {
        const data = await response.json();
        setEntitlement(data);
      }
    } catch (error) {
      console.error('Error checking entitlement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingCards = async () => {
    setCardsLoading(true);
    setCardsError(null);
    
    try {
      const response = await apiGet('/api/billing/cards');
      
      if (!response.ok) {
        throw new Error('Failed to load billing plans');
      }
      
      const data = await response.json();
      setBillingCards(data.cards || []);
    } catch (error) {
      console.error('Error fetching billing cards:', error);
      setCardsError(error.message || 'Failed to load plans');
    } finally {
      setCardsLoading(false);
    }
  };

  const handleSubscribe = async (card) => {
    // Prevent double clicks
    if (checkoutLoading[card.stripe_price_id]) {
      return;
    }

    try {
      // Set loading for this specific card
      setCheckoutLoading(prev => ({ ...prev, [card.stripe_price_id]: true }));
      setStatusMessage(null);

      const originUrl = window.location.origin;

      const response = await apiPost('/api/billing/create-checkout-session', {
        origin_url: originUrl,
        price_id: card.stripe_price_id,
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle validation error from backend
        if (errorData.detail?.error === 'Invalid plan') {
          throw new Error('This plan is unavailable. Please refresh.');
        }
        
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe Checkout (in-app browser on native, full-page on web)
      if (data.url) {
        await openExternal(data.url);
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to start checkout. Please try again.'
      });
      // Clear loading state on error
      setCheckoutLoading(prev => ({ ...prev, [card.stripe_price_id]: false }));
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessingPortal(true);
      setStatusMessage(null);

      const originUrl = window.location.origin;

      const response = await apiPost('/api/billing/create-portal-session', { origin_url: originUrl });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to open billing portal');
      }

      const data = await response.json();

      // Redirect to Stripe Customer Portal (in-app browser on native, full-page on web)
      if (data.url) {
        await openExternal(data.url);
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to open billing portal. Please try again.'
      });
      setProcessingPortal(false);
    }
  };

  // Show for trial users or expired users - pricing card view
  const showPricingCards = entitlement && (
    entitlement.status === 'trial_active' || 
    entitlement.status === 'expired'
  );

  // Show for active paid users - manage subscription view
  const showManageView = entitlement && (
    entitlement.status === 'paid_active' || 
    entitlement.status === 'canceled_pending_end'
  );

  return (
    <div className="billing-page" data-testid="billing-page">
      <div className="billing-container">
        <button 
          className="back-button"
          onClick={() => navigate('/app')}
          data-testid="back-to-app"
        >
          <ArrowLeft size={20} />
          Back to App
        </button>

        {/* Status Message */}
        {statusMessage && (
          <div className={`status-message ${statusMessage.type}`} data-testid="status-message">
            {statusMessage.type === 'pending' && <Loader size={20} className="spinner" />}
            {statusMessage.type === 'success' && <CheckCircle size={20} />}
            {statusMessage.type === 'error' && <AlertCircle size={20} />}
            <span>{statusMessage.text}</span>
          </div>
        )}

        {/* Billing Not Configured */}
        {!billingConfigured && !loading && (
          <div className="billing-not-configured" data-testid="billing-not-configured">
            <AlertCircle size={48} />
            <h2>Billing Not Configured</h2>
            <p>The billing system is not yet set up. Please contact support for assistance.</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="billing-loading">
            <Loader size={48} className="spinner" />
            <p>Loading your subscription details...</p>
          </div>
        )}

        {/* Suspended Account Warning */}
        {!loading && entitlement?.is_suspended && (
          <div className="entitlement-banner suspended" data-testid="suspended-banner">
            <ShieldAlert size={24} />
            <div className="banner-content">
              <h3>Account Suspended</h3>
              <p>Your account has been suspended. Please contact support to resolve this issue.</p>
              <a href="mailto:idreesrahman04@gmail.com" className="contact-link">
                Contact Support
              </a>
            </div>
          </div>
        )}

        {/* Pricing Cards View - For Trial & Expired Users */}
        {billingConfigured && !loading && !entitlement?.is_suspended && showPricingCards && (
          <div className="pricing-section">
            {/* Header */}
            <div className="pricing-header">
              <h1 className="pricing-title">
                {entitlement?.is_trial 
                  ? 'Continue Your Journey with Hourtrix Pro' 
                  : 'Get Started with Hourtrix Pro'}
              </h1>
              <p className="pricing-subtitle">
                {entitlement?.is_trial 
                  ? `You have ${entitlement.days_remaining || 0} days left in your free trial`
                  : 'Choose the plan that works for you'}
              </p>
            </div>

            {/* Trial Status Badge */}
            {entitlement?.is_trial && (
              <div className="trial-status-badge" data-testid="trial-badge">
                <Sparkles size={16} />
                <span>Free Trial Active - {entitlement.days_remaining || 0} days remaining</span>
              </div>
            )}

            {/* Cards Loading State */}
            {cardsLoading && (
              <div className="cards-loading">
                <Loader size={32} className="spinner" />
                <p>Loading available plans...</p>
              </div>
            )}

            {/* Cards Error State */}
            {cardsError && (
              <div className="cards-error">
                <AlertCircle size={32} />
                <p>{cardsError}</p>
                <button onClick={fetchBillingCards} className="retry-button">
                  <RefreshCw size={16} />
                  Retry
                </button>
              </div>
            )}

            {/* Empty Cards State */}
            {!cardsLoading && !cardsError && billingCards.length === 0 && (
              <div className="cards-empty">
                <AlertCircle size={32} />
                <p>No plans available at this time.</p>
                <button onClick={fetchBillingCards} className="retry-button">
                  <RefreshCw size={16} />
                  Refresh
                </button>
              </div>
            )}

            {/* Pricing Cards Grid */}
            {!cardsLoading && !cardsError && billingCards.length > 0 && (
              <div className="pricing-cards-grid">
                {billingCards.map((card) => (
                  <div 
                    key={card.stripe_price_id} 
                    className={`pricing-card ${card.highlight_badge ? 'featured' : ''}`}
                    data-testid="pricing-card"
                  >
                    {/* Highlight Badge */}
                    {card.highlight_badge && (
                      <div className="card-badge">{card.highlight_badge}</div>
                    )}

                    {/* Card Header */}
                    <div className="card-header">
                      <h3 className="plan-name">{card.public_title}</h3>
                      
                      {/* Subtitle */}
                      {card.public_subtitle && (
                        <p className="plan-subtitle">{card.public_subtitle}</p>
                      )}

                      {/* Price Display */}
                      <div className="price-container">
                        <span className="price-display">
                          {card.resolved_price_display}
                        </span>
                        <span className="price-interval">
                          {card.resolved_interval_label}
                        </span>
                      </div>
                    </div>

                    {/* Benefits List */}
                    <div className="card-features">
                      <h4>What's included:</h4>
                      <ul>
                        {card.benefits.map((benefit, index) => (
                          <li key={index}>
                            <Check size={18} className="check-icon" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    <button
                      className="subscribe-button-primary"
                      onClick={() => handleSubscribe(card)}
                      disabled={checkoutLoading[card.stripe_price_id]}
                      data-testid={`subscribe-button-${card.stripe_price_id}`}
                    >
                      {checkoutLoading[card.stripe_price_id] ? (
                        <>
                          <Loader size={20} className="spinner" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard size={20} />
                          {card.button_text}
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manage Subscription View - For Active Paid Users */}
        {billingConfigured && !loading && !entitlement?.is_suspended && showManageView && (
          <div className="manage-subscription-section">
            {/* Active Subscription Banner */}
            <div className="entitlement-banner active" data-testid="active-banner">
              <CheckCircle size={24} />
              <div className="banner-content">
                <h3>Subscription Active</h3>
                <p>
                  Status: {entitlement.status_display}
                  {entitlement.access_end_at && (
                    <>
                      {' • '}
                      {entitlement.status === 'canceled_pending_end' ? (
                        <span className="access-end-warning">
                          Access ends on {new Date(entitlement.access_end_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span>Renews on {new Date(entitlement.access_end_at).toLocaleDateString()}</span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="billing-content">
              <div className="billing-icon">
                <CreditCard size={48} />
              </div>
              
              <h1 className="billing-title">Manage Your Subscription</h1>
              
              <div className="billing-action">
                <h2>Subscription Portal</h2>
                <p className="billing-description">
                  Update payment methods, view billing history, or manage your subscription.
                </p>
                <button
                  className="portal-button"
                  onClick={handleManageSubscription}
                  disabled={processingPortal}
                  data-testid="manage-subscription-button"
                >
                  {processingPortal ? (
                    <>
                      <Loader size={20} className="spinner" />
                      Opening Portal...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={20} />
                      Open Billing Portal
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingPage;
